import React, { useMemo, useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import './FleetDetailsPage.less';
import { BaseTabs, TabPanel } from 'app/components/Tabs';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FleetDetailsTabs } from './constants';
import { BackActionSplitCardContentLayout } from 'app/components/layouts';
import {
  MachineOwnerCard,
  SharedUserAccess,
} from 'app/components/access-sharing/cards';
import useHasAccessSharingPermissions from 'app/hooks/useHasAccessSharingPermissions';
import FleetImage from 'assets/images/ill_fleet_fleet-details.png';
import { ActionsListItem, EllipsisMenu } from 'app/components/menu';
import { AddIcon } from 'app/components/icons';
import { AccessSharedWith } from 'app/components/access-sharing';
import { EquipmentInfo } from 'app/components/access-sharing';
import { DeleteDialog } from 'app/components/dialogs';
import {
  ChangeUserFleetPermissionsDialog,
  EditFleetDialog,
  EditFleetNameDialog,
} from 'app/components/access-sharing/dialogs';
import { ShareAccessEquipmentGuideProvider } from 'app/hooks/access-sharing';
import { formatDate } from 'utils/date';
import useGetEquipmentData from 'app/hooks/useGetEquipmentData';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import { useNotificationsContext } from 'context/useNotificationsContext';
import { api } from 'services/api';
import { ErrorNotification, Success } from 'app/components/Notifications';
import { SharedItemShareType } from 'services/api/shares';
import { orderBy } from 'lodash';

export default function FleetDetailsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [deleteFleet] = api.useDeleteFleetMutation();
  const [updateFleet] = api.useUpdateFleetMutation();
  const { addNotification, addSnackbar } = useNotificationsContext();
  const { addErrorModal } = React.useContext(GlobalErrorContext);
  const [revokeInvitation] = api.useRevokeInvitationMutation();
  const { sharesData, equipments } = useGetEquipmentData();

  const fleet = sharesData?.fleets.find(
    fleet => fleet?.sharedItemDetails.exposedId === id,
  );

  const hasAccess = useHasAccessSharingPermissions(
    fleet?.sharedItemDetails.myRole,
  );

  const [currentTab, setCurrentTab] = useState<
    keyof typeof FleetDetailsTabs | string
  >(FleetDetailsTabs.EQUIPMENT);

  // More appropriate entity name will be set when API is ready.
  const [selectedUserAccessToChange, setSelectedUserAccessToChange] = useState<
    SharedUserAccess | undefined
  >();
  const isChangeUserPermissionsDialogOpen = !!selectedUserAccessToChange;

  const [isDeleteFleetDialogOpen, setIsDeleteFleetDialogOpen] = useState(false);

  const [isFleetNameUpdating, setIsFleetNameUpdating] = useState(false);
  const [isEditFleetNameDialogOpen, setIsEditFleetNameDialogOpen] =
    useState(false);

  const [isFleetUpdating, setIsFleetUpdating] = useState(false);
  const [isEditFleetDialogOpen, setIsEditFleetDialogOpen] = useState(false);

  const [isRevokeAllUserAccessDialogOpen, setIsRevokeAllUserAccessDialogOpen] =
    useState(false);

  const [selectedUserAccessToRevoke, setSelectedUserAccessToRevoke] = useState<
    SharedUserAccess | undefined
  >();
  const isRevokeUserAccessDialogOpen = !!selectedUserAccessToRevoke;

  const userAccess = useMemo(() => {
    const activeUserAccess = fleet?.sharedWith
      .filter(
        sharedWith =>
          !sharedWith.endsAt || new Date(sharedWith.endsAt) > new Date(),
      )
      .map(sharedWith => ({
        id: sharedWith.exposedAccessSharingRecordId,
        firstName: sharedWith.displayName,
        lastName: '',
        email: sharedWith.email,
        role: sharedWith.sharedRole,
        startTime: sharedWith.startsAt
          ? formatDate(sharedWith.startsAt, 'DATE_FORMAT_DATETIME')
          : undefined,
        endTime: sharedWith.endsAt
          ? formatDate(sharedWith.endsAt, 'DATE_FORMAT_DATETIME')
          : undefined,
      }));

    const expiredUserAccess = fleet?.sharedWith
      .filter(
        sharedWith =>
          sharedWith.endsAt && new Date(sharedWith.endsAt) < new Date(),
      )
      .map(sharedWith => ({
        id: sharedWith.exposedAccessSharingRecordId,
        firstName: sharedWith.displayName,
        lastName: '',
        email: sharedWith.email,
        role: sharedWith.sharedRole,
        startTime: sharedWith.startsAt
          ? formatDate(sharedWith.startsAt, 'DATE_FORMAT_DATETIME')
          : undefined,
        endTime: sharedWith.endsAt
          ? formatDate(sharedWith.endsAt, 'DATE_FORMAT_DATETIME')
          : undefined,
      }));

    return {
      activeUserAccess: orderBy(activeUserAccess, ['firstName'], ['asc']),
      expiredUserAccess: orderBy(expiredUserAccess, ['firstName'], ['asc']),
    };
  }, [fleet?.sharedWith]);

  const fleetEquipments = useMemo(() => {
    const equipmentList = fleet?.sharedItemDetails.exposedEquipmentIds
      ?.map(equipmentId => {
        const equipment = equipments?.find(
          equipment => equipment.id === equipmentId,
        );
        return equipment;
      })
      .filter((equipment): equipment is NonNullable<typeof equipment> =>
        Boolean(equipment),
      );
    const equipmentMachines = equipmentList?.filter(e => e.type === 'machine');
    const equipmentAttatchments = equipmentList?.filter(
      e => e.type !== 'machine',
    );
    const sortedEquipmentMachines = orderBy(
      equipmentMachines,
      e => (e.nickName || e.model).toLowerCase() || !e.hasTelematics,
      ['asc'],
    );
    const sortedEquipmentAttatchments = orderBy(
      equipmentAttatchments,
      e => (e.nickName || e.model).toLowerCase(),
      ['asc'],
    );
    const orderedEquipmentList = [
      ...sortedEquipmentMachines,
      ...sortedEquipmentAttatchments,
    ];

    return orderedEquipmentList;
  }, [fleet?.sharedItemDetails.exposedEquipmentIds, equipments]);

  const tabs = useMemo(
    () => [
      {
        label: t('equipment'),
        badgeContent: fleet?.sharedItemDetails.exposedEquipmentIds?.length ?? 0,
        value: FleetDetailsTabs.EQUIPMENT,
      },
      {
        label: t('shared_with'),
        badgeContent:
          (userAccess.activeUserAccess?.length ?? 0) +
          (userAccess.expiredUserAccess?.length ?? 0),
        value: FleetDetailsTabs.SHARED_WITH,
      },
    ],
    [t, fleet?.sharedItemDetails.exposedEquipmentIds?.length, userAccess],
  );

  const headerActions = useMemo<ActionsListItem[]>(() => {
    return [
      {
        label: t('access_sharing.role_based_actions.edit_fleet_name'),
        action: () => setIsEditFleetNameDialogOpen(true),
        exclude: !hasAccess('editFleetName'),
      },
      {
        label: t('access_sharing.role_based_actions.delete_fleet'),
        action: () => setIsDeleteFleetDialogOpen(true),
        exclude: !hasAccess('deleteFleet'),
      },
    ];
  }, [hasAccess, t]);

  const tabsActions = useMemo<ActionsListItem[]>(() => {
    return [
      {
        label: t('access_sharing.role_based_actions.remove_all_users'),
        action: () => setIsRevokeAllUserAccessDialogOpen(true),
        exclude: !hasAccess('revokeAllUsersAccess'),
      },
    ];
  }, [hasAccess, t]);

  function handleBack() {
    navigate('/access/equipment-and-fleets');
  }

  const handleChangeFleetName = async (name: string) => {
    if (!fleet?.sharedItemDetails.exposedId) return;

    try {
      setIsFleetNameUpdating(true);
      await updateFleet({
        exposedFleetId: fleet.sharedItemDetails.exposedId,
        name,
      }).unwrap();

      addNotification(
        <Success
          text={t('access_sharing.notifications.fleet_successfully_updated')}
        />,
      );
      setIsEditFleetNameDialogOpen(false);
    } catch (error) {
      console.error(error);
      addErrorModal();
    } finally {
      setIsFleetNameUpdating(false);
    }
  };

  const handleChangeFleet = async ({
    name,
    exposedEquipmentIds,
  }: {
    name: string;
    exposedEquipmentIds: string[];
  }) => {
    if (!fleet?.sharedItemDetails.exposedId) return;

    try {
      setIsFleetUpdating(true);
      await updateFleet({
        exposedFleetId: fleet.sharedItemDetails.exposedId,
        name,
        exposedEquipmentIds,
      }).unwrap();

      addNotification(
        <Success
          text={t('access_sharing.notifications.fleet_successfully_updated')}
        />,
      );
      setIsEditFleetDialogOpen(false);
    } catch (error) {
      console.error(error);
      addErrorModal();
    } finally {
      setIsFleetUpdating(false);
    }
  };

  const handleDeleteFleet = async () => {
    if (!fleet?.sharedItemDetails.exposedId) return;

    try {
      await deleteFleet({
        exposedFleetId: fleet?.sharedItemDetails.exposedId,
      }).unwrap();

      addNotification(
        <Success
          text={t('access_sharing.notifications.fleet_successfully_deleted')}
        />,
      );
      setIsDeleteFleetDialogOpen(false);
    } catch (error) {
      console.error(error);
      addErrorModal();
    }
  };

  const handleRevokeAllAccess = async () => {
    try {
      const exposedAccessSharingRecordIds = fleet?.sharedWith.map(
        sharedWith => sharedWith.exposedAccessSharingRecordId,
      );

      if (!exposedAccessSharingRecordIds) return;

      await revokeInvitation(
        exposedAccessSharingRecordIds.map(id => ({
          shareType: SharedItemShareType.FLEET,
          exposedAccessSharingRecordId: id,
        })),
      );

      addSnackbar(
        <div className="snackbar-container">
          <span>
            {t('access_sharing.snackbars.you_removed_all_users_access')}
          </span>
        </div>,
      );
    } catch (error) {
      console.error(error);
      addNotification(<ErrorNotification text={'Server error'} />);
    } finally {
      setIsRevokeAllUserAccessDialogOpen(false);
    }
  };

  const handleRevokeAccess = async () => {
    try {
      const exposedAccessSharingRecordId = fleet?.sharedWith.find(
        sharedWith => sharedWith.email === selectedUserAccessToRevoke?.email,
      )?.exposedAccessSharingRecordId;

      if (!exposedAccessSharingRecordId) return;

      await revokeInvitation([
        {
          shareType: 'fleet',
          exposedAccessSharingRecordId,
        },
      ]);
      addNotification(
        <Success text={t('access_sharing.snackbars.access-revoked')} />,
      );
    } catch (error) {
      console.error(error);
      addNotification(<ErrorNotification text={'Server error'} />);
    } finally {
      setSelectedUserAccessToRevoke(undefined);
    }
  };

  function renderTabActinButtons() {
    const isEquipmentTabActive = currentTab === FleetDetailsTabs.EQUIPMENT;
    const isSharedWithTabActive = currentTab === FleetDetailsTabs.SHARED_WITH;

    const hasAccessToAddNewUser = hasAccess('addNewUser');
    const hasAccessToEditFleet = hasAccess('editFleet');
    const hasAccessToRevokeAllAccess = hasAccess('revokeAllUsersAccess');
    const hasAccessToAnything =
      hasAccessToAddNewUser ||
      hasAccessToEditFleet ||
      hasAccessToRevokeAllAccess;

    if (!hasAccessToAnything) {
      return null;
    }

    return (
      <div className="fleet-details-page__user-actions">
        {isEquipmentTabActive && hasAccessToEditFleet && (
          <Button
            className="fleet-details-page__action-btn"
            themeColor="secondary"
            onClick={() => setIsEditFleetDialogOpen(true)}
          >
            {t('access_sharing.role_based_actions.edit_fleet')}
          </Button>
        )}

        {isSharedWithTabActive && hasAccessToAddNewUser && (
          <>
            <Button
              className="fleet-details-page__action-btn fleet-details-page__add-new-user-btn"
              themeColor="secondary"
              onClick={() =>
                navigate(
                  `/access/shared-fleet/${fleet?.sharedItemDetails.exposedId}/add-new-user`,
                )
              }
            >
              <AddIcon strokeWidth={2} />
              {t('add_user')}
            </Button>

            <EllipsisMenu actions={tabsActions} />
          </>
        )}
      </div>
    );
  }

  // TODO Request a design
  if (!fleet) {
    return <h1>Fleet Not Found</h1>;
  }

  return (
    <>
      <BackActionSplitCardContentLayout
        className="fleet-details-page"
        backActionProps={{ onBack: handleBack }}
        renderHeader={() => (
          <div className="fleet-details-page__header">
            <div className="fleet-details-page__info-and-picture-wrapper">
              <div className="fleet-details-page__info-wrapper">
                <div className="fleet-details-page__title">
                  <h1 className="fleet-details-page__nickname ellipsis">
                    {fleet.sharedItemDetails.sharedItemLabel}
                  </h1>
                  <EllipsisMenu actions={headerActions} />
                </div>

                <MachineOwnerCard
                  email={fleet.sharedItemDetails.ownerEmail}
                  firstName={fleet.sharedItemDetails.ownerName}
                  lastName={''}
                  className="fleet-details-page__owner-card fleet-details-page__owner-card_desktop"
                />
              </div>

              <div className="fleet-details-page__picture">
                <img src={FleetImage} alt="Fleet" />
              </div>
            </div>

            <MachineOwnerCard
              email={fleet.sharedItemDetails.ownerEmail}
              firstName={fleet.sharedItemDetails.ownerName}
              lastName={''}
              className="fleet-details-page__owner-card fleet-details-page__owner-card_mobile"
            />

            <div className="fleet-details-page__tabs-and-actions-wrapper">
              <BaseTabs
                className="fleet-details-page__tabs"
                tabs={tabs}
                value={currentTab}
                onChange={setCurrentTab}
              />

              {renderTabActinButtons()}
            </div>
          </div>
        )}
        renderContent={() => (
          <div className="fleet-details-page__content">
            <TabPanel
              value={FleetDetailsTabs.EQUIPMENT}
              currentTab={currentTab}
            >
              {fleetEquipments?.map(equipment => (
                <EquipmentInfo
                  key={equipment.id}
                  className="fleet-details-page__equipment-info-card"
                  equipment={equipment}
                />
              ))}
            </TabPanel>

            <TabPanel
              value={FleetDetailsTabs.SHARED_WITH}
              currentTab={currentTab}
            >
              <AccessSharedWith
                className="fleet-details-page__shared-with"
                role={fleet.sharedItemDetails.myRole}
                userAccess={userAccess.activeUserAccess ?? []}
                expiredUserAccess={userAccess.expiredUserAccess ?? []}
                onChangeAccess={setSelectedUserAccessToChange}
                onRevokeAccess={setSelectedUserAccessToRevoke}
              />
            </TabPanel>
          </div>
        )}
      />

      {isEditFleetNameDialogOpen && (
        <EditFleetNameDialog
          initialValue={fleet.sharedItemDetails.sharedItemLabel}
          isLoading={isFleetNameUpdating}
          onChange={handleChangeFleetName}
          onClose={() => setIsEditFleetNameDialogOpen(false)}
        />
      )}

      <ShareAccessEquipmentGuideProvider>
        {isEditFleetDialogOpen && (
          <EditFleetDialog
            initialName={fleet.sharedItemDetails.sharedItemLabel}
            exposedEquipmentIds={fleet.sharedItemDetails.exposedEquipmentIds}
            isLoading={isFleetUpdating}
            onChange={handleChangeFleet}
            onClose={() => setIsEditFleetDialogOpen(false)}
          />
        )}
      </ShareAccessEquipmentGuideProvider>

      {isChangeUserPermissionsDialogOpen && (
        <ChangeUserFleetPermissionsDialog
          fleet={fleet}
          userAccess={selectedUserAccessToChange}
          onClose={() => setSelectedUserAccessToChange(undefined)}
        />
      )}

      <DeleteDialog
        open={isDeleteFleetDialogOpen}
        title={t('access_sharing.dialogs.delete_fleet.title', {
          name: fleet.sharedItemDetails.sharedItemLabel,
        })}
        confirmationLabel={t(
          'access_sharing.dialogs.delete_fleet.actions.delete',
        )}
        onClose={() => setIsDeleteFleetDialogOpen(false)}
        onConfirm={handleDeleteFleet}
      />

      <DeleteDialog
        open={isRevokeUserAccessDialogOpen}
        title={t('revoke_access_confirmation', {
          email: selectedUserAccessToRevoke?.email,
          machineName: fleet.sharedItemDetails.sharedItemLabel,
        })}
        confirmationLabel={t('access_sharing.role_based_actions.revoke')}
        onClose={() => setSelectedUserAccessToRevoke(undefined)}
        onConfirm={handleRevokeAccess}
      />

      <DeleteDialog
        open={isRevokeAllUserAccessDialogOpen}
        title={t('revoke_access_confirmation_all', {
          machineName: fleet.sharedItemDetails.sharedItemLabel,
        })}
        confirmationLabel={t('access_sharing.role_based_actions.revoke')}
        onClose={() => setIsRevokeAllUserAccessDialogOpen(false)}
        onConfirm={handleRevokeAllAccess}
      />
    </>
  );
}
